/**
 * Frontend Stripe service — callable Cloud Function wrappers
 *
 * The publishable key (VITE_STRIPE_PUBLISHABLE_KEY) determines test vs live mode:
 *   pk_test_... → Stripe test mode (safe for dev, use test cards)
 *   pk_live_... → Stripe live mode (real transactions)
 *
 * Both modes show transactions in the Stripe Dashboard under the matching mode tab.
 */

import { loadStripe } from '@stripe/stripe-js';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Guard: emulator mode must always use the development Stripe sandbox.
// If VITE_STRIPE_ENV is anything other than 'development' while emulators are
// active (VITE_USE_EMULATORS=true), throw immediately — before any key is loaded.
const stripeEnv = import.meta.env.VITE_STRIPE_ENV as string | undefined;
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

if (useEmulators && stripeEnv !== 'development') {
  throw new Error(
    `[Stripe] Emulator is running with VITE_STRIPE_ENV="${stripeEnv ?? 'unset'}". ` +
    'The emulator must only use the development Stripe sandbox. ' +
    'Check that frontend/.env.emulator has VITE_STRIPE_ENV=development and the correct pk_test_ key.'
  );
}

// Guard: live keys must ONLY be used in production builds.
// This catches misconfigurations at runtime before any payment is attempted.
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

if (!stripeKey) {
  console.error('[Stripe] VITE_STRIPE_PUBLISHABLE_KEY is not set.');
} else if (stripeKey.startsWith('pk_live_') && !import.meta.env.PROD) {
  // Throw so this surfaces immediately in dev/staging — don't silently charge real cards
  throw new Error(
    '[Stripe] A LIVE publishable key was detected in a non-production build. ' +
    'Set VITE_STRIPE_PUBLISHABLE_KEY=pk_test_... in your .env.development / .env.staging / .env.emulator file.'
  );
} else if (stripeKey.startsWith('pk_test_') && import.meta.env.PROD) {
  // Warn loudly in production if test key is used — payments will fail silently for real users
  console.warn(
    '[Stripe] A TEST publishable key is being used in a production build. ' +
    'Set VITE_STRIPE_PUBLISHABLE_KEY=pk_live_... in your .env.production file.'
  );
}

// Singleton Stripe instance — created once when this module is imported
export const stripePromise = loadStripe(stripeKey ?? '');

const fns = functions;

// ── Payment decline messages ──────────────────────────────────────────────────

import { formatStripeDeclineError } from '../utils/stripeErrors';
export { formatStripeDeclineError };

// ── Payment Intent ────────────────────────────────────────────────────────────

export interface CreatePaymentIntentParams {
  coachId: string;
  offeringId: string;
  amountCents: number;
  saveCard?: boolean;
  paymentMethodId?: string;
  sessionId?: string;
  /** Submit attempt within one booking session; joins the server's
   *  idempotency key so a retry is a new operation, not a replay. */
  attempt?: number;
}

export interface CreatePaymentIntentResult {
  clientSecret: string | null;
  paymentIntentId: string;
  /** true when the server confirmed the PI using a saved card — no client confirmCardPayment needed */
  alreadyConfirmed: boolean;
}

const _createPaymentIntentCallable = httpsCallable<
  CreatePaymentIntentParams,
  CreatePaymentIntentResult
>(fns, 'createPaymentIntent');

/**
 * Wrapper around the createPaymentIntent callable that maps "decline:<code>"
 * error messages (set by the Cloud Function) to user-friendly strings before
 * propagating. This ensures the decline mapping is applied once, at the source,
 * regardless of which catch block handles the error further up the stack.
 */
export async function createPaymentIntentFn(
  params: CreatePaymentIntentParams,
): Promise<Awaited<ReturnType<typeof _createPaymentIntentCallable>>> {
  try {
    return await _createPaymentIntentCallable(params);
  } catch (err) {
    const msg = err instanceof Error ? err.message : '';
    if (msg.startsWith('decline:')) {
      throw new Error(formatStripeDeclineError(msg.slice('decline:'.length), undefined));
    }
    throw err;
  }
}

// ── Setup Intent (save card) ──────────────────────────────────────────────────

export interface CreateSetupIntentResult {
  clientSecret: string;
}

export const createSetupIntentFn = httpsCallable<void, CreateSetupIntentResult>(
  fns,
  'createSetupIntent'
);

// ── COPPA verifiable parental consent (auth + void on $0.50) ─────────────────

export interface VerifyParentWithCardParams {
  paymentMethodId: string;
  playerFirstName: string;
  /** Child's DOB as ISO date string (YYYY-MM-DD). */
  playerDOB: string;
  parentFirstName: string;
  parentLastName: string;
  /** Parent's typed legal name from Step 3. */
  parentalConsentSignature: string;
  // Child's tennis info from the parent-child-tennis step (all optional).
  childUtr?: number | null;
  childNtrp?: number | null;
  childPlayerType?: string | null;
  childLocation?: string | null;
  /** Writes to canonical playerProfile.goal. */
  childTrainingGoal?: string | null;
}

export interface VerifyParentWithCardResult {
  success: boolean;
}

/**
 * Performs the FTC-approved credit-card-verification VPC method:
 * - Creates a $0.50 PaymentIntent with capture_method: 'manual'
 * - Cancels (voids) it immediately — no money is ever captured
 * - Writes the parent + child + VPC fields to the user doc atomically
 * Requires the caller to be the just-signed-in parent (context.auth.uid).
 */
export const verifyParentWithCardFn = httpsCallable<
  VerifyParentWithCardParams,
  VerifyParentWithCardResult
>(fns, 'verifyParentWithCard');

// ── Payment Methods ───────────────────────────────────────────────────────────

export interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export const listPaymentMethodsFn = httpsCallable<void, { paymentMethods: SavedCard[] }>(
  fns,
  'listPaymentMethods'
);

export const detachPaymentMethodFn = httpsCallable<{ paymentMethodId: string }, { success: boolean }>(
  fns,
  'detachPaymentMethod'
);

// ── Stripe Connect (Coach) ────────────────────────────────────────────────────

export const createConnectedAccountFn = httpsCallable<void, { accountId: string }>(
  fns,
  'createConnectedAccount'
);

export const createAccountLinkFn = httpsCallable<
  { accountId: string; returnUrl: string; refreshUrl: string },
  { url: string }
>(fns, 'createAccountLink');

export const refreshStripeAccountStatusFn = httpsCallable<
  void,
  { status: 'pending' | 'connected'; payoutsEnabled: boolean; chargesEnabled: boolean }
>(fns, 'refreshStripeAccountStatus');

export const adminRefreshStripeAccountStatusFn = httpsCallable<
  { coachId: string },
  { status: 'pending' | 'connected'; payoutsEnabled: boolean; chargesEnabled: boolean }
>(fns, 'adminRefreshStripeAccountStatus');

// ── Payments / Refunds ────────────────────────────────────────────────────────

export const refundLessonFn = httpsCallable<{ lessonId: string }, { refundId?: string; message?: string; creditRefundTransactionId?: string }>(
  fns,
  'refundLesson'
);

export const transferToCoachFn = httpsCallable<{ lessonId: string }, { transferId?: string; message?: string }>(
  fns,
  'transferToCoach'
);

export const payCoachReferralFn = httpsCallable<{ payoutId: string }, { transferId?: string; message?: string }>(
  fns,
  'payCoachReferral'
);
